import { useState, useEffect } from "react";
import { useRole } from "./useRole";
import { useToast } from "./use-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  fetchProfileData,
  fetchMentorData,
  fetchPlayerSocialData,
  updateProfileData,
  updateMentorData,
  updatePlayerSocialData,
  uploadProfileImage,
  removeProfileImage,
  ProfileData,
  MentorInfoData
} from "@/services/profileService";

interface UseProfileReturn {
  profile: ProfileData | null;
  mentorInfo: any | null;
  playerSocial: any | null;
  loading: boolean;
  error: Error | null;
  updateProfile: (data: Partial<ProfileData>) => Promise<void>;
  updateMentorInfo: (data: Partial<MentorInfoData>) => Promise<void>;
  updatePlayerSocial: (data: Partial<ProfileData>) => Promise<void>;
  uploadImage: (file: File, type: 'profile' | 'banner') => Promise<string | null>;
  removeImage: (type: 'profile' | 'banner') => Promise<boolean>;
}

export function useProfile(): UseProfileReturn {
  const { role, authUser } = useRole();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [mentorInfo, setMentorInfo] = useState<any | null>(null);
  const [playerSocial, setPlayerSocial] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Carrega dados do perfil
  useEffect(() => {
    const loadProfileData = async () => {
      if (!authUser?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Carregar dados básicos do perfil
        const profileData = await fetchProfileData(authUser.id);
        setProfile(profileData);

        // Carrega dados específicos baseado na role
        if (role === 'mentor') {
          console.log('🔄 [useProfile] Carregando dados do mentor para:', authUser.id);
          const mentorData = await fetchMentorData(authUser.id);
          console.log('✅ [useProfile] Dados do mentor carregados:', {
            id: mentorData.id,
            bio: mentorData.bio,
            biography: mentorData.biography,
            youtube_url: mentorData.youtube_url,
            instagram_url: mentorData.instagram_url,
            discord_url: mentorData.discord_url,
            twitch_url: mentorData.twitch_url,
            achievements: mentorData.achievements
          });
          setMentorInfo(mentorData);
        } else if (role === 'jogador') {
          const socialData = await fetchPlayerSocialData(authUser.id);
          setPlayerSocial(socialData);
        }
        setError(null);
      } catch (err: any) {
        console.error("[useProfile] Erro ao carregar dados do perfil:", err);
        setError(err);
        toast({
          title: "Erro ao carregar perfil",
          description: err.message || "Ocorreu um erro ao carregar os dados do perfil.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [authUser, role, toast]);

  // Função para atualizar dados básicos do perfil
  const updateProfile = async (data: Partial<ProfileData>) => {
    if (!authUser?.id) return;
    
    try {
      setLoading(true);
      await updateProfileData(authUser.id, {
        nickname: data.nickname,
        preferred_game: data.preferred_game
      });
      
      // Atualiza o estado local
      setProfile(prev => prev ? { ...prev, ...data } : null);
      
      toast({
        title: "Perfil atualizado",
        description: "Seus dados foram atualizados com sucesso."
      });
    } catch (err: any) {
      console.error("[useProfile] Erro ao atualizar perfil:", err);
      toast({
        title: "Erro ao atualizar perfil",
        description: err.message || "Ocorreu um erro ao salvar os dados.",
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar dados do mentor
  const updateMentorInfo = async (data: Partial<MentorInfoData>) => {
    if (!authUser?.id || role !== 'mentor') return;
    
    try {
      setLoading(true);
      console.log('🔄 [updateMentorInfo] Iniciando atualização para:', authUser.id);
      console.log('📝 [updateMentorInfo] Dados recebidos:', JSON.stringify(data, null, 2));
      
      // Separa dados básicos dos dados específicos do mentor
      const { nickname, preferred_game, ...mentorData } = data;
      
      // Atualiza dados básicos se fornecidos
      if (nickname || preferred_game) {
        console.log('👤 [updateMentorInfo] Atualizando dados básicos:', JSON.stringify({ nickname, preferred_game }, null, 2));
        await updateProfileData(authUser.id, {
          nickname,
          preferred_game
        });
        
        // Atualiza estado local
        setProfile(prev => prev ? { ...prev, nickname, preferred_game } : null);
      }
      
      // Atualiza dados do mentor
      console.log('🎯 [updateMentorInfo] Atualizando dados do mentor:', JSON.stringify(mentorData, null, 2));
      const updatedData = await updateMentorData(authUser.id, {
        biography: mentorData.biography,
        bio: mentorData.biography, // Mantém ambos os campos sincronizados
        youtube_url: mentorData.youtube_url,
        instagram_url: mentorData.instagram_url,
        discord_url: mentorData.discord_url,
        twitch_url: mentorData.twitch_url,
        video_url: mentorData.video_url, // 🎯 Campo para vídeo da página pública
        achievements: mentorData.achievements
      });
      
      // Atualiza estado local
      setMentorInfo(prev => {
        const updated = { ...prev, ...mentorData };
        console.log('🔄 [updateMentorInfo] Estado local atualizado:', JSON.stringify(updated, null, 2));
        return updated;
      });
      
      // Obtém informações para invalidar todos os caches relacionados
      const mentorId = updatedData?.[0]?.id || mentorInfo?.id;
      const slug = profile?.slug;
      
      console.log('🔄 [updateMentorInfo] Invalidando caches...');
      console.log('🔄 [updateMentorInfo] Profile ID:', authUser.id);
      console.log('🔄 [updateMentorInfo] Mentor ID:', mentorId);
      console.log('🔄 [updateMentorInfo] Slug:', slug);
      
      // Invalida TODOS os possíveis caches relacionados ao mentor
      const queriesToInvalidate = [
        ["mentor-details", authUser.id],  // Cache por profile_id
      ];
      
      if (mentorId) {
        queriesToInvalidate.push(["mentor-details", mentorId]); // Cache por mentor_id
      }
      
      if (slug) {
        queriesToInvalidate.push(["mentor-details", slug]); // Cache por slug
      }
      
      // Invalida cada cache específico
      for (const queryKey of queriesToInvalidate) {
        console.log('🔄 [updateMentorInfo] Invalidando cache:', queryKey);
        queryClient.invalidateQueries({ queryKey });
      }
      
      // Invalida todos os caches mentor-details como fallback
      console.log('🔄 [updateMentorInfo] Invalidando TODOS os caches mentor-details');
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          return query.queryKey[0] === "mentor-details";
        }
      });
      
      // Força um refetch
      queryClient.refetchQueries({ 
        predicate: (query) => {
          return query.queryKey[0] === "mentor-details";
        }
      });
      
      toast({
        title: "Perfil atualizado",
        description: "Seus dados foram atualizados com sucesso."
      });
    } catch (err: any) {
      console.error("[useProfile] Erro ao atualizar dados do mentor:", err);
      toast({
        title: "Erro ao atualizar perfil",
        description: err.message || "Ocorreu um erro ao salvar os dados.",
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar dados sociais do jogador
  const updatePlayerSocial = async (data: Partial<ProfileData>) => {
    if (!authUser?.id || role !== 'jogador') return;
    
    try {
      setLoading(true);
      
      // Separa dados básicos dos dados sociais
      const { nickname, preferred_game, biography, youtube_url, instagram_url, discord_url, twitch_url } = data;
      
      // Atualiza dados básicos se fornecidos
      if (nickname || preferred_game) {
        await updateProfileData(authUser.id, {
          nickname,
          preferred_game
        });
        
        // Atualiza estado local
        setProfile(prev => prev ? { ...prev, nickname, preferred_game } : null);
      }
      
      // Atualiza dados sociais
      await updatePlayerSocialData(authUser.id, {
        biography,
        youtube_url,
        instagram_url,
        discord_url,
        twitch_url
      });
      
      // Atualiza estado local
      setPlayerSocial(prev => ({ ...prev, biography, youtube_url, instagram_url, discord_url, twitch_url }));
      
      toast({
        title: "Perfil atualizado",
        description: "Seus dados foram atualizados com sucesso."
      });
    } catch (err: any) {
      console.error("[useProfile] Erro ao atualizar dados sociais:", err);
      toast({
        title: "Erro ao atualizar perfil",
        description: err.message || "Ocorreu um erro ao salvar os dados.",
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Função para upload de imagens
  const uploadImage = async (file: File, type: 'profile' | 'banner'): Promise<string | null> => {
    if (!authUser?.id) return null;
    
    try {
      console.log(`🔄 [useProfile] Iniciando upload de ${type} para usuário:`, authUser.id);
      
      const imageUrl = await uploadProfileImage(authUser.id, file, type);
      
      console.log(`✅ [useProfile] Upload de ${type} concluído:`, imageUrl);
      
      // Atualiza o estado local
      if (type === 'profile') {
        setMentorInfo(prev => prev ? { ...prev, image_url: imageUrl } : null);
      } else {
        setMentorInfo(prev => prev ? { ...prev, cover_url: imageUrl } : null);
      }
      
      toast({
        title: "Upload concluído",
        description: "Sua imagem foi carregada com sucesso."
      });
      
      return imageUrl;
    } catch (err: any) {
      console.error("[useProfile] Erro no upload da imagem:", err);
      toast({
        title: "Erro no upload",
        description: err.message || "Ocorreu um erro ao enviar a imagem.",
        variant: "destructive"
      });
      return null;
    }
  };

  // Função para remover imagens
  const removeImage = async (type: 'profile' | 'banner'): Promise<boolean> => {
    if (!authUser?.id) return false;
    
    try {
      console.log(`🔄 [useProfile] Removendo ${type} para usuário:`, authUser.id);
      
      await removeProfileImage(authUser.id, type);
      
      console.log(`✅ [useProfile] Remoção de ${type} concluída`);
      
      // Atualiza o estado local
      if (type === 'profile') {
        setMentorInfo(prev => prev ? { ...prev, image_url: null } : null);
      } else {
        setMentorInfo(prev => prev ? { ...prev, cover_url: null } : null);
      }
      
      toast({
        title: "Imagem removida",
        description: "A imagem foi removida com sucesso."
      });
      
      return true;
    } catch (err: any) {
      console.error("[useProfile] Erro ao remover imagem:", err);
      toast({
        title: "Erro ao remover imagem",
        description: err.message || "Ocorreu um erro ao remover a imagem.",
        variant: "destructive"
      });
      return false;
    }
  };

  return {
    profile,
    mentorInfo,
    playerSocial,
    loading,
    error,
    updateProfile,
    updateMentorInfo,
    updatePlayerSocial,
    uploadImage,
    removeImage
  };
}
